import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Input, Popover, Space, Tooltip } from 'antd'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GLOBAL_CLIENT } from 'src/helpers/GlobalClient'
import { normalizeUrlString } from 'src/helpers/constants'

/**
 * O hien link trong bang quan ly viec (cot URL, cot Link design).
 *
 * Bang co rat nhieu cot nen o nay phai hep: chi hien link dau tien (cat bot cho
 * vua), con lai gom thanh "+N". Muon sua thi bam vao o -> mo popover rong voi
 * mot o nhap duy nhat, MOI DONG LA MOT LINK. Kieu nay go/dan nhanh hon la
 * nhieu o Input rieng, va Ctrl+Enter la luu duoc ngay.
 *
 * Gia tri luu xuong server van la chuoi ngan cach ', ' — giong cho designer nop
 * url o popup chi tiet — nen hai noi doc/ghi lan nhau khong lech dinh dang.
 */

const toList = (value?: string) => (typeof value === 'string' && value !== '' ? value.split(', ') : [])

const withProtocol = (url: string) => (/^https?:\/\//i.test(url) ? url : `http://${url}`)

type JobLinkCellProps = {
   value?: string
   // Khong truyen onSave => o chi de xem, khong hien nut sua
   onSave?: (value: string) => Promise<boolean>
   placeholder?: string
   title?: string
}

const JobLinkCell: React.FC<JobLinkCellProps> = ({ value, onSave, placeholder, title }) => {
   const { t } = useTranslation()
   const [open, setOpen] = useState(false)
   const [draft, setDraft] = useState('')
   const [isSaving, setIsSaving] = useState(false)
   const list = toList(value)
   const editable = typeof onSave === 'function'

   // Mo popover thi nap lai noi dung dang luu, moi dong mot link
   useEffect(() => {
      if (open) setDraft(list.join('\n'))
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open])

   const handleSave = async () => {
      if (!onSave) return
      const next = normalizeUrlString(draft.split('\n').join(', '))
      try {
         setIsSaving(true)
         const ok = await onSave(next)
         if (ok) setOpen(false)
      } finally {
         setIsSaving(false)
      }
   }

   const renderLinks = () => {
      if (list.length === 0) {
         return <span style={{ color: '#b0b0b0' }}>{placeholder || '—'}</span>
      }
      return (
         <Tooltip title={list.join('\n')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 420 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}>
               <a
                  href={withProtocol(list[0])}
                  target="_blank"
                  rel="noopener noreferrer external"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                     display: 'inline-block',
                     maxWidth: 110,
                     overflow: 'hidden',
                     textOverflow: 'ellipsis',
                     whiteSpace: 'nowrap',
                     verticalAlign: 'bottom'
                  }}
               >
                  {list[0]}
               </a>
               {list.length > 1 && <span style={{ marginLeft: 4, color: '#8c8c8c' }}>{`+${list.length - 1}`}</span>}
            </span>
         </Tooltip>
      )
   }

   const editForm = (
      <div style={{ width: 420 }}>
         <div style={{ marginBottom: 8, color: '#8c8c8c', fontSize: 12 }}>
            {'Mỗi dòng một link. Ctrl + Enter để lưu.'}
         </div>
         <Input.TextArea
            autoFocus
            value={draft}
            rows={4}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
               if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault()
                  handleSave()
               }
            }}
            placeholder={placeholder}
         />
         <Space style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setOpen(false)}>
               {t(GLOBAL_CLIENT.huy)}
            </Button>
            <Button size="small" type="primary" loading={isSaving} onClick={handleSave}>
               {t(GLOBAL_CLIENT.save)}
            </Button>
         </Space>
      </div>
   )

   if (!editable) {
      return <div className="job-link-cell">{renderLinks()}</div>
   }

   return (
      <div className="job-link-cell">
         {renderLinks()}
         <Popover
            visible={open}
            onVisibleChange={setOpen}
            trigger="click"
            placement="bottomRight"
            title={title}
            content={editForm}
            destroyTooltipOnHide
         >
            <span className="job-link-cell__edit gx-link" title={title}>
               {list.length === 0 ? <PlusOutlined /> : <EditOutlined />}
            </span>
         </Popover>
      </div>
   )
}

export default JobLinkCell
